import { useState, useEffect } from "react";
import PushFeedback from "./PushFeedback";
import { useLegacyPushNotification } from "../../hooks/push-notifications";
import { AlertDialogButtonType, useAlertDialog } from "../shared/AlertDialog";
import {
  useCreatePushNotification,
  usePushAudiencePreview,
  usePushImageActions,
  usePushUsers,
} from "../../hooks/push-notifications";

function LegacyPushNotificationForm({ notification }) {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [imageAsset, setImageAsset] = useState(null);
  const [topic, setTopic] = useState("");
  const [platform, setPlatform] = useState("all");
  const [errors, setErrors] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const send = useLegacyPushNotification();
  const { confirm } = useAlertDialog();

  const { uploadImage } = usePushImageActions();

  useEffect(() => {
    if (notification) {
      setTitle(notification.title || "");
      setMessage(notification.message || "");
      setPlatform(notification.platform || "all");
    }
  }, [notification]);

  const requestConfirmation = () => {
    const validationErrors = [];
    if (title.trim().length < 5) {
      validationErrors.push(
        "Notification title must be at least 5 characters.",
      );
    }
    if (message.trim().length < 10) {
      validationErrors.push(
        "Notification message must be at least 10 characters.",
      );
    }
    setErrors(validationErrors);
    if (!validationErrors.length) {
      confirm({
        title: "Send notification",
        children: (
          <>
            <p className="mb-4 text-sm">This sends notification immediately.</p>
            <dl className="grid grid-cols-[8rem_1fr] gap-2 text-sm">
              <dt>Title</dt>
              <dd>{title}</dd>
              <dt>Message</dt>
              <dd>{message}</dd>
              <dt>Platform</dt>
              <dd>{platform}</dd>
              {topic.trim() ? (
                <>
                  <dt>Topic</dt>
                  <dd>{topic.trim()}</dd>
                </>
              ) : null}
            </dl>
          </>
        ),
        confirmAction: {
          label: "Ok",
          onClick: submit,
          type: AlertDialogButtonType.PRIMARY,
        },
        cancelAction: {
          label: "Close",
          type: AlertDialogButtonType.DESTRUCTIVE,
        },
      });
    }
  };

  const submit = () => {
    const request = { title: title.trim(), message: message.trim() };
    if (topic.trim()) {
      request.topic = topic.trim();
    }
    if (imageAsset != null) {
      request.imageAssetId = imageAsset.id;
    }

    setErrors([]);
    send.mutate(
      { platform, request },
      {
        onSuccess: (response) => {
          setSuccessMessage(response?.message ?? "Notification sent.");
          setTitle("");
          setMessage("");
          setTopic("");
        },
        onError: (error) => setErrors([error.message]),
      },
    );
  };

  const handleImage = (file) => {
    setErrors([]);
    if (!file) return;
    const allowedTypes = ["image/png", "image/jpeg"];
    const maxBytes = 1_048_576;
    if (!allowedTypes.includes(file.type) || file.size > maxBytes) {
      setErrors(["Image must be a PNG or JPEG no larger than 1 MiB."]);
      return;
    }
    uploadImage.mutate(
      { file },
      {
        onSuccess: (asset) => setImageAsset(asset),
        onError: (error) => setErrors([error.message]),
      },
    );
  };

  return (
    <>
      <PushFeedback errors={errors} message={successMessage} />
      <div className="grid gap-5">
        <label className="text-sm font-semibold">
          Title <span aria-hidden="true">*</span>
          <input
            aria-label="Title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            className="mt-2 w-full rounded border border-slate-200 bg-white px-3 py-3 font-normal shadow-sm"
          />
        </label>
        <label className="text-sm font-semibold">
          Message <span aria-hidden="true">*</span>
          <textarea
            aria-label="Message"
            rows="5"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            className="mt-2 w-full rounded border border-slate-200 bg-white px-3 py-3 font-normal shadow-sm"
          />
        </label>
        <label className="text-sm font-semibold">
          Topic <span className="font-normal">(optional)</span>
          <input
            aria-label="Topic"
            value={topic}
            onChange={(event) => setTopic(event.target.value)}
            className="mt-2 w-full rounded border border-slate-200 bg-white px-3 py-3 font-normal shadow-sm"
          />
          <span className="mt-2 block text-xs font-normal text-slate-500">
            The tenant code is added automatically before delivery.
          </span>
        </label>
        <fieldset>
          <legend className="mb-2 text-sm font-semibold">Platform</legend>
          <div className="flex flex-wrap gap-6">
            {[
              ["android", "Android"],
              ["ios", "iOS"],
              ["all", "Both"],
            ].map(([value, label]) => (
              <label key={value} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="legacy-platform"
                  checked={platform === value}
                  onChange={() => setPlatform(value)}
                />
                {label}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="mt-4">
          <label className="text-sm font-semibold">
            Image <span className="font-normal">(optional)</span>
            <input
              aria-label="Image"
              type="file"
              accept="image/png,image/jpeg"
              disabled={uploadImage.isPending}
              onChange={(event) => handleImage(event.target.files?.[0])}
              className="mt-2 block w-full text-sm font-normal"
            />
          </label>
          {uploadImage.isPending ? (
            <p className="mt-2 text-sm">Uploading image…</p>
          ) : null}
          {imageAsset ? (
            <div className="mt-3 flex items-center gap-4 rounded border border-slate-200 p-3">
              <img
                src={imageAsset.url}
                alt="Notification preview"
                className="h-20 w-20 rounded object-cover"
              />
              <div className="text-sm">
                <div>Uploaded image #{imageAsset.id}</div>
                <button
                  type="button"
                  onClick={() => setImageAsset(null)}
                  className="mt-2 text-red-700 underline"
                >
                  Remove from notification
                </button>
              </div>
            </div>
          ) : null}
        </div>

        <div className="mt-6">
          <button
            type="button"
            disabled={send.isPending}
            onClick={requestConfirmation}
            className="rounded bg-primary text-white font-bold uppercase text-xs px-8 py-2 shadow hover:shadow-md outline-none focus:outline-none disabled:bg-gray-400 mr-1 ease-linear transition-all duration-150"
          >
            {send.isPending ? "Sending…" : "Send Notification"}
          </button>
        </div>
      </div>
    </>
  );
}

export default LegacyPushNotificationForm;
